/**
 * LifecycleSection — miolo do Tenant Lifecycle Dashboard (Brief 4 T13), extraído
 * de `pages/TenantLifecyclePage.tsx` (Admin Hub T11).
 *
 * O corpo é o MESMO de `TenantLifecycleDashboardContent` — cards por status,
 * filtros, lista paginada de tenants e o drawer de detalhe (que carrega o
 * wizard de churn, `ChurningInitiationWizard`) — menos o `<h1>` de página
 * ("Tenant Lifecycle Dashboard"): dentro de uma `DomainSection` o título já
 * vem do catálogo (`item.label`); dentro de `pages/TenantLifecyclePage.tsx`
 * (rota antiga, só viva pelo próprio `TenantLifecyclePage.spec.tsx` — a rota
 * `/admin/tenant-lifecycle` já redireciona para `/admin/conta#lifecycle`
 * desde a T3 do Admin Hub) o `<h1>` próprio compõe por fora.
 *
 * O gate de LEITURA (`tenant-lifecycle-read`) NÃO mora aqui — quem compõe
 * decide (o `DomainSection` do hub, via catálogo `lifecycle` →
 * `tenant-lifecycle-read`; ou `TenantLifecyclePage()` da rota antiga, que
 * mantém o próprio double-gate D-B pré-existente — fora do escopo desta
 * task, intocado para não quebrar as 30 specs próprias). `canTransition`
 * (`tenant-lifecycle-transition`) continua resolvido AQUI — governa AÇÃO
 * (badge "PODE TRANSICIONAR" + ações no drawer), não leitura, e é ortogonal
 * ao gate de leitura de quem quer que componha esta seção.
 *
 * O wizard de churn (`ChurningInitiationWizard`, dentro de
 * `TenantLifecycleDetailDrawer`) é ação de RISCO: botões e confirmação
 * próprios, fora de qualquer `StickySaveBar` — nada nesta seção dá a ele um
 * caminho para lá (mesmo tratamento que `OperationProfileTab` já recebeu na
 * T9). Esta seção é 100% leitura + ações próprias — nenhuma escrita nova.
 *
 * ⚠️ Dívida PRÉ-EXISTENTE, DECLARADA (não corrigida aqui — "mover, não
 * reescrever"): este arquivo usa estilo inline com hex cravado (ex.: `style=
 * {{ fontSize: 22, color: '#111827' }}`), fora dos tokens do Design System
 * (`packages/design-system/src/tokens.css`). Modernizar para as classes DS
 * canônicas é outra task, com seu próprio veredito visual — mover a UI como
 * está evita que uma "correção" silenciosa se disfarce de refactor de
 * localização num diff já grande.
 */

import { useState } from 'react';

import { useAuth } from '../../../hooks/use-auth';
import { useCapability } from '../../../hooks/use-capability';
import { useTenantLifecycleDashboard } from '../../../hooks/use-tenant-lifecycle-dashboard';

import { StatusTotalsCards } from './StatusTotalsCards';
import { TenantLifecycleDetailDrawer } from './TenantLifecycleDetailDrawer';
import { TenantStatusBadge } from './TenantStatusBadge';

import type { PackageType, TenantStatus } from './types';
import type { TenantLifecycleDashboardParams } from '../../../hooks/use-tenant-lifecycle-dashboard';
import type { TenantLifecycleSnapshotWire } from '../../../lib/api-client';

export function LifecycleSection(): JSX.Element {
  const { user } = useAuth();
  const canTransition = useCapability('tenant-lifecycle-transition');
  const [statusFilter, setStatusFilter] = useState<TenantStatus | undefined>(undefined);
  const [packageFilter, setPackageFilter] = useState<PackageType | undefined>(undefined);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const dashParams: TenantLifecycleDashboardParams = { limit: 25 };
  if (statusFilter !== undefined) dashParams.status = statusFilter;
  if (packageFilter !== undefined) dashParams.packageType = packageFilter;
  if (cursor !== undefined) dashParams.cursor = cursor;

  const { data, isLoading, isError, refetch } = useTenantLifecycleDashboard(dashParams);

  const PACKAGE_LABELS: Record<PackageType, string> = {
    solo: 'Solo',
    team: 'Team',
    professional: 'Professional',
    regulated: 'Regulated',
  };

  return (
    <div style={{ padding: '20px 24px', minHeight: '100%', backgroundColor: '#f9fafb' }}>
      {/* Page header */}
      <div style={{ marginBottom: 20 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: '#9ca3af',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginBottom: 2,
          }}
        >
          Estado contratual · transições auditadas
        </div>
        {/* `<h1>` próprio saiu (Admin Hub T11) — DomainSection já supre título
            quando esta seção é composta no hub; a rota antiga (`pages/
            TenantLifecyclePage.tsx`) prove o próprio `<h1>` por fora. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => {
                void refetch();
              }}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                backgroundColor: '#fff',
                fontSize: 12,
                cursor: 'pointer',
                color: '#374151',
              }}
            >
              Atualizar
            </button>
          </div>
        </div>
        {user && (
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
            Operando como{' '}
            <strong>{user.primaryRole === 'gestor' ? 'Gestor' : 'Admin Tenant'}</strong>
            {canTransition && (
              <span
                style={{
                  marginLeft: 8,
                  fontSize: 10,
                  backgroundColor: '#dbeafe',
                  color: '#1e40af',
                  borderRadius: 4,
                  padding: '1px 5px',
                  fontWeight: 600,
                }}
              >
                PODE TRANSICIONAR
              </span>
            )}
          </div>
        )}
      </div>

      {/* Totals cards — filtro por status */}
      {data && (
        <StatusTotalsCards
          totals={data.totals}
          activeFilter={statusFilter}
          onFilterChange={setStatusFilter}
        />
      )}

      {/* Filtro por packageType */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>Tier:</span>
        <button
          type="button"
          aria-pressed={packageFilter === undefined}
          onClick={() => {
            setPackageFilter(undefined);
            setCursor(undefined);
          }}
          style={filterBtnStyle(packageFilter === undefined)}
        >
          Todos
        </button>
        {(['solo', 'team', 'professional', 'regulated'] as PackageType[]).map((p) => (
          <button
            key={p}
            type="button"
            aria-pressed={packageFilter === p}
            onClick={() => {
              setPackageFilter(p);
              setCursor(undefined);
            }}
            style={filterBtnStyle(packageFilter === p)}
          >
            {PACKAGE_LABELS[p]}
          </button>
        ))}

        {(statusFilter !== undefined || packageFilter !== undefined) && (
          <button
            type="button"
            onClick={() => {
              setStatusFilter(undefined);
              setPackageFilter(undefined);
              setCursor(undefined);
            }}
            style={{
              padding: '4px 10px',
              borderRadius: 20,
              border: '1px solid #fca5a5',
              backgroundColor: '#fef2f2',
              color: '#991b1b',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Limpar filtros
          </button>
        )}
      </div>

      {/* Loading */}
      {isLoading && <TenantListSkeleton />}

      {/* Error */}
      {isError && !isLoading && (
        <div
          style={{
            padding: 20,
            textAlign: 'center',
            color: '#ef4444',
            fontSize: 13,
          }}
        >
          Erro ao carregar dashboard de lifecycle. Tente atualizar.
        </div>
      )}

      {/* Lista */}
      {data && !isLoading && (
        <>
          {data.items.length === 0 ? (
            <div
              style={{
                padding: 40,
                textAlign: 'center',
                color: '#9ca3af',
                fontSize: 14,
              }}
            >
              Nenhum tenant encontrado com os filtros aplicados.
            </div>
          ) : (
            <>
              <div
                role="table"
                aria-label="Lista de Tenants — Lifecycle"
                style={{
                  backgroundColor: '#fff',
                  borderRadius: 10,
                  border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                }}
              >
                {/* Table header */}
                <div
                  role="row"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 90px 110px 120px 90px',
                    padding: '10px 16px',
                    backgroundColor: '#f9fafb',
                    borderBottom: '1px solid #e5e7eb',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#6b7280',
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    gap: 12,
                  }}
                >
                  <div role="columnheader">Tenant</div>
                  <div role="columnheader">Tier</div>
                  <div role="columnheader">Status</div>
                  <div role="columnheader">Ativado em</div>
                  <div role="columnheader">Ações</div>
                </div>

                {/* Rows */}
                {data.items.map((tenant) => (
                  <TenantLifecycleRow
                    key={tenant.id}
                    tenant={tenant}
                    onSelect={() => {
                      setSelectedTenantId(tenant.id);
                    }}
                  />
                ))}
              </div>

              {/* Pagination */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: 12,
                  fontSize: 12,
                  color: '#6b7280',
                }}
              >
                <span>{data.items.length} tenant(s) exibido(s)</span>
                <div style={{ display: 'flex', gap: 8 }}>
                  {cursor && (
                    <button
                      type="button"
                      onClick={() => {
                        setCursor(undefined);
                      }}
                      style={paginationBtnStyle()}
                    >
                      ← Primeira página
                    </button>
                  )}
                  {data.nextCursor && (
                    <button
                      type="button"
                      onClick={() => {
                        setCursor(data.nextCursor ?? undefined);
                      }}
                      style={paginationBtnStyle()}
                    >
                      Próxima →
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* Detail Drawer */}
      {selectedTenantId && (
        <TenantLifecycleDetailDrawer
          tenantId={selectedTenantId}
          onClose={() => {
            setSelectedTenantId(null);
          }}
        />
      )}
    </div>
  );
}

// ─── sub-components ───────────────────────────────────────────────────────

function TenantLifecycleRow({
  tenant,
  onSelect,
}: {
  tenant: TenantLifecycleSnapshotWire;
  onSelect: () => void;
}): JSX.Element {
  return (
    <div
      role="row"
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 90px 110px 120px 90px',
        padding: '12px 16px',
        borderBottom: '1px solid #f3f4f6',
        fontSize: 13,
        alignItems: 'center',
        gap: 12,
        cursor: 'default',
        transition: 'background-color 0.1s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#f9fafb';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <div role="cell">
        <div style={{ fontWeight: 600, color: '#111827' }}>{tenant.name}</div>
        <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{tenant.slug}</div>
      </div>
      <div role="cell">
        <span
          style={{
            fontSize: 11,
            padding: '2px 6px',
            borderRadius: 4,
            backgroundColor: '#f3f4f6',
            color: '#6b7280',
            fontWeight: 600,
          }}
        >
          {tenant.packageType}
        </span>
      </div>
      <div role="cell">
        <TenantStatusBadge status={tenant.status as TenantStatus} />
      </div>
      <div role="cell" style={{ color: '#6b7280', fontSize: 12 }}>
        {tenant.activatedAt ? new Date(tenant.activatedAt).toLocaleDateString('pt-BR') : '—'}
      </div>
      <div role="cell">
        <button
          type="button"
          onClick={onSelect}
          style={{
            padding: '5px 10px',
            borderRadius: 6,
            border: '1px solid #d1d5db',
            backgroundColor: '#fff',
            fontSize: 12,
            cursor: 'pointer',
            color: '#374151',
            fontWeight: 500,
          }}
        >
          Detalhes
        </button>
      </div>
    </div>
  );
}

function TenantListSkeleton(): JSX.Element {
  return (
    <div aria-busy="true" aria-label="Carregando lista de tenants...">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          style={{
            height: 56,
            borderRadius: 8,
            backgroundColor: '#e5e7eb',
            marginBottom: 6,
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}

// ─── style helpers ────────────────────────────────────────────────────────

function filterBtnStyle(isActive: boolean): React.CSSProperties {
  return {
    padding: '4px 10px',
    borderRadius: 20,
    border: '1px solid',
    borderColor: isActive ? '#6366f1' : '#d1d5db',
    backgroundColor: isActive ? '#ede9fe' : '#fff',
    color: isActive ? '#4f46e5' : '#374151',
    fontSize: 12,
    cursor: 'pointer',
  };
}

function paginationBtnStyle(): React.CSSProperties {
  return {
    padding: '5px 12px',
    borderRadius: 6,
    border: '1px solid #d1d5db',
    backgroundColor: '#fff',
    fontSize: 12,
    cursor: 'pointer',
    color: '#374151',
  };
}
